/**
 * Kompakt driftsgrunnlag som sendes med hver melding til assistenten.
 * Kun tall som allerede er beregnet i portalen (samme motor som Oversikt/Rapport) – modellen får aldri rådata
 * den kan regne feil på, og aldri tall som ikke finnes i visningene.
 */
import type { Metric, PeriodResult, MachineAggregate } from './engine';
import type { ComparisonRow } from './compare';
import type { Deviation, Scenario } from '@/domain/types';

export interface AssistantContextInput {
  current: PeriodResult;
  reference: PeriodResult | null;
  comparison: { rows: ComparisonRow[] } | null;
  deviations: Deviation[];
  scenario: Scenario;
  fromDate: string;
  toDate: string;
  compare: { fromDate: string; toDate: string };
  compareMode: string;
  scope: string;
  role: string;
}

function num(v: number | null | undefined, decimals = 3): number | null {
  if (v === null || v === undefined || !Number.isFinite(v)) return null;
  const f = 10 ** decimals;
  return Math.round(v * f) / f;
}

function metric(m: Metric | undefined) {
  if (!m) return null;
  return {
    label: m.label,
    value: num(m.value, Math.min(m.decimals + 2, 4)),
    unit: m.unit,
    status: m.status,
    ...(m.reason ? { reason: m.reason } : {}),
    ...(m.provenance ? { provenance: m.provenance } : {}),
    ...(m.isIllustrative ? { illustrative: true } : {}),
  };
}

function machine(mc: MachineAggregate) {
  return {
    id: mc.machineId,
    name: mc.name,
    type: mc.type,
    activity: mc.activity,
    engineHours: num(mc.engineHours, 2),
    idleHours: num(mc.idleHours, 2),
    idleShare: num(mc.idleShare, 4),
    fuelLitres: num(mc.fuelLitres, 1),
    co2Kg: num(mc.co2Kg, 0),
    co2Source: mc.co2Source,
    quality: mc.quality,
    ...(mc.missingFields.length ? { missingFields: mc.missingFields } : {}),
  };
}

export function buildAssistantContext(r: AssistantContextInput) {
  const c = r.current;
  const keys = ['tonnes', 'dieselPerTonne', 'fuelLitres', 'engineHours', 'idleHours', 'idleShare', 'machineHoursPerTonne', 'productionHours', 'stopHours', 'productionRate', 'co2TotalKg', 'co2PerTonne', 'costPerTonne'] as const;
  const kpi: Record<string, unknown> = {};
  for (const k of keys) kpi[k] = metric((c as unknown as Record<string, Metric>)[k]);

  const comparison = (r.comparison?.rows ?? []).map((row) => ({
    key: row.key,
    label: row.label,
    current: num(row.current.value, 4),
    reference: num(row.reference.value, 4),
    absChange: num(row.absChange, 4),
    pctChange: num(row.pctChange, 2),
    ppChange: num(row.ppChange, 2),
    unit: row.unit,
    status: row.status,
    ...(row.message ? { message: row.message } : {}),
  }));

  const machines = [...c.machines].sort((a, b) => (b.fuelLitres ?? 0) - (a.fuelLitres ?? 0)).slice(0, 12).map(machine);

  const deviations = r.deviations.slice(0, 8).map((d) => ({
    id: d.id,
    rule: d.ruleLabel,
    severity: d.severity,
    subject: d.scope.machineId ?? d.scope.lineId,
    observed: d.observed,
    observation: d.observation,
    possibleExplanation: d.possibleExplanation,
    nextCheck: d.nextCheck,
  }));

  const days = c.days
    .filter((d) => d.kind !== 'planned_off')
    .map((d) => ({ date: d.date, tonnes: num(d.tonnes, 0), litresPerTonne: num(d.litresPerTonne, 3), idleShare: num(d.idleShare, 3), productionHours: num(d.productionHours, 2), stopHours: num(d.stopHours, 2), coverage: d.coverage }));

  return {
    periode: { fra: r.fromDate, til: r.toDate, tidssone: 'Europe/Oslo', produksjonsdager: c.productionDays },
    referanse: { fra: r.compare.fromDate, til: r.compare.toDate, modus: r.compareMode },
    scenario: { id: r.scenario.id, navn: r.scenario.name },
    omfang: c.scopeLabel,
    rolle: r.role,
    datagrunnlag: {
      komplett: c.basisComplete,
      manglendeFelt: c.missingFields,
      dekning: c.coverage.map((f) => ({ felt: f.field, mottatt: f.received, forventet: f.expected, status: f.status, kilde: f.sourceId })),
      linjetilstandMaalt: c.lineStateMeasured,
    },
    noekkeltall: kpi,
    co2: {
      kilde: c.co2.source,
      kildeTekst: c.co2.sourceLabel,
      rapportertKg: num(c.co2.reportedKg, 0),
      estimertKg: num(c.co2.estimatedKg, 0),
      intervaller: { rapportert: c.co2.reportedIntervals, estimert: c.co2.estimatedIntervals, mangler: c.co2.missingIntervals, totalt: c.co2.totalIntervals },
      faktor: c.co2.factor,
      faktorEnhet: c.co2.factorUnit,
      faktorKilde: c.co2.factorSource,
      metode: c.co2.method,
      prioritetsregel: c.co2.priorityRule,
    },
    satser: { dieselNokPerLiter: c.rates.dieselNokPerLitre, maskintimeNok: c.rates.machineNokPerHour, merknad: 'Illustrative forutsetninger – ikke selvkost eller verifiserte lokale satser.' },
    sammenligning: comparison,
    maskiner: machines,
    avvik: deviations,
    dager: days,
    advarsler: c.warnings.slice(0, 10).map((w) => ({ kode: w.code, alvor: w.severity, melding: w.message })),
  };
}
